import DOMPurify from 'dompurify'

const BLOCKED_EMAIL_TAGS = [
  'base', 'embed', 'form', 'iframe', 'input', 'link', 'math', 'meta',
  'object', 'script', 'select', 'style', 'svg', 'textarea', 'video', 'audio',
]
const REMOTE_IMAGE_PROTOCOLS = new Set(['https:'])
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])

export interface InboundAttachmentRef {
  filename: string
  content_type: string
  size: number
  url: string
  content_id?: string | null
}

export interface RenderableEmail {
  html: string
  blockedRemoteImageCount: number
}

function isAllowedRemoteImage(url: string): boolean {
  try {
    return REMOTE_IMAGE_PROTOCOLS.has(new URL(url, window.location.origin).protocol)
  } catch {
    return false
  }
}

function isSafeLink(url: string): boolean {
  try {
    return SAFE_LINK_PROTOCOLS.has(new URL(url, window.location.origin).protocol)
  } catch {
    return false
  }
}

/**
 * Produces safe, isolated reader markup for received mail. Sender content is
 * not trusted with dashboard DOM access, and tracking assets remain opt-in.
 */
// CHECKPOINT: PRD-10 FR-10.14.5 — Safely render inbound mailbox message content and inline attachments.
export function prepareInboundEmailForRendering(
  emailHtml: string,
  attachments: InboundAttachmentRef[] | undefined,
  loadRemoteImages: boolean,
): RenderableEmail {
  const clean = DOMPurify.sanitize(emailHtml, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: BLOCKED_EMAIL_TAGS,
  })
  const cidUrlMap = new Map<string, string>()
  for (const attachment of attachments || []) {
    if (attachment.content_id && attachment.url) {
      cidUrlMap.set(String(attachment.content_id).replace(/[<>]/g, ''), attachment.url)
    }
  }

  const documentFragment = document.createElement('div')
  documentFragment.innerHTML = clean
  let blockedRemoteImageCount = 0

  documentFragment.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
    const style = element.getAttribute('style') || ''
    // CSS url() values can load tracking pixels just like an img src.
    element.setAttribute('style', style.replace(/url\(\s*[^)]*\)/gi, ''))
  })

  documentFragment.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    const href = link.getAttribute('href') || ''
    if (!isSafeLink(href)) {
      link.removeAttribute('href')
      return
    }
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })

  documentFragment.querySelectorAll<HTMLImageElement>('img').forEach((image) => {
    const source = image.getAttribute('src') || ''
    const lowerSource = source.toLowerCase()

    if (lowerSource.startsWith('cid:')) {
      const contentId = source.slice(4).trim().replace(/[<>]/g, '')
      const attachmentUrl = cidUrlMap.get(contentId)
      if (attachmentUrl && isAllowedRemoteImage(attachmentUrl)) {
        image.setAttribute('src', attachmentUrl)
      } else {
        image.removeAttribute('src')
        image.setAttribute('alt', image.alt || 'Inline image unavailable')
      }
      return
    }

    if (lowerSource.startsWith('data:image/')) return

    if (isAllowedRemoteImage(source)) {
      if (loadRemoteImages) return
      blockedRemoteImageCount += 1
      image.removeAttribute('src')
      image.setAttribute('alt', image.alt || 'Remote image hidden')
      return
    }

    image.removeAttribute('src')
    image.setAttribute('alt', image.alt || 'Image unavailable')
  })

  return { html: documentFragment.innerHTML, blockedRemoteImageCount }
}

// CHECKPOINT: PRD-10 FR-10.14.4 — Preserve readable message presentation in dashboard dark/light themes.
export function createInboundEmailDocument(emailHtml: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="referrer" content="no-referrer">
    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; img-src data: https:; style-src 'unsafe-inline'; font-src data:; media-src 'none'; connect-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'; frame-src 'none'">
    <style>
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      html, body { min-width: 0; margin: 0; background: #ffffff; color: #1f1d2b; }
      body { padding: 1.25rem; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.55; overflow-wrap: anywhere; }
      img { max-width: 100% !important; height: auto !important; }
      table { max-width: 100% !important; }
      pre { white-space: pre-wrap; }
      a { color: #6841d8; }
    </style>
  </head>
  <body>${emailHtml}</body>
</html>`
}
